import { ChangeEvent, KeyboardEvent, useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useChatMessages } from '@/hooks/useChatMessages';
import { useCreateNewChat } from '@/hooks';
import { useEventCallback } from '@/hooks/useEventCallback';
import {
  clearChat,
  clearDocuments,
  clearSessionDocuments,
  selectChatId,
  selectSessionChatDocuments,
  useAppDispatch,
  useAppSelector,
  setChatDocumentPanelSettings,
  openChatHistoryRowPanel,
  markDocumentUnviewable,
  selectUnviewableDocumentIds,
  ChatDocumentPanelSettings,
  promoteSessionDocuments,
  setImprovedPromptForChat,
  clearImprovedPromptForChat,
  selectImprovedPromptsForChat,
  selectChatDocumentsDownload,
  selectPendingMessageForChat,
  selectDeferredSendForChat,
  selectDeferredSendErrorForChat,
  setPendingMessageForChat,
  clearPendingMessageForChat,
  setDeferredSendForChat,
  clearDeferredSendForChat,
  ImprovedPromptEntry,
  selectUserPreferences,
  setPendingUrlSyncChatId,
  setIgnoreSharedLinkChatIdSync,
} from '@/store';
import {
  clearImprovePromptAbortController,
  getImprovePromptAbortController,
  setImprovePromptAbortController,
} from '../improvePromptAbortStore';
import { cancelDeferredChatSend } from './cancelDeferredChatSend';
import { isRtkFetchErrorWithStatus } from '@/api/utils';
import { showDocumentPreviewUnsupportedToast } from '../utils/unviewableChatDocument';
import { isDisplayableDocument, isRenderedFromOriginal } from '@/utils/getDocumentViewerKind';
import { getCitedDocumentHistoryRowId, resolveCitationTarget } from '../utils/resolveCitationTarget';
import {
  useGetChatDocumentsQuery,
  useImprovePromptMutation,
  useImprovePromptClarifiersMutation,
  ClarifierQuestion,
  ImprovePromptQuestionAnswer,
} from '@/api/chat';
import { useChatFilesUpload } from './useChatFilesUpload';
import { globalToastService } from '@repo/atomic-components';
import {
  insertLineBreak,
  isChatSendShortcut,
  isModifiedEnter as isModifiedEnterKey,
  isPlainEnter as isPlainEnterKey,
} from '@/ui/features/chat/utils/chatEnterKey';
import { Citation, ChatMessage } from '@/types/chat';
import type { ChatSendMessageType, MessageAttachedDocument, SendMessageOptions } from '@/types/chat';
import {
  resolveChatSendMessageType,
  type PendingToolkitPromptPick,
  type SuggestionArrowKind,
} from '../utils/resolveChatSendMessageType';
import { AppRoutes, draftChat, MessageRole } from '@/constants';
import {
  normalizeMarkdownBullets,
  getBulletSpaceTransform,
  getBulletEnterTransform,
  getBulletBackspaceTransform,
} from '@/utils/chat';
import { ERROR_STATUS_VALUES, FileStatus, isProcessingStatus, VALID_STATUS_VALUES } from '@/types/file';
import { useChatFileProcessingPolling } from './useChatFileProcessingPolling';
import { MAX_FILES_PER_QUERY } from '@/ui/constants/chat';
import {
  filterFilesByType,
  getUnsupportedFormatMessage,
  getUnsupportedFormatToast,
  sortByUpdatedAtDesc,
} from '../utils/chat';
import { renameDuplicateFiles } from '../utils/renameDuplicateFiles';
import type { SelectedAnswer } from '../components/ImprovePromptQuestions';
import { useRestoreDeferredSendAfterNavigation } from './useRestoreDeferredSendAfterNavigation';
import { useRestoreSessionDocumentsForDeferredSend } from './useRestoreSessionDocumentsForDeferredSend';
import { useRestorePromptAfterDeferredSendError } from './useRestorePromptAfterDeferredSendError';
import { useRestoreUnsentChatDocuments } from './useRestoreUnsentChatDocuments';
import { useUnsentPrompt } from './useUnsentPrompt';
import { useChatAutoScroll } from './useChatAutoScroll';
import {
  mergeMatterDocumentIdsForSend,
  normalizePendingMatterDocumentIds,
} from '../matterChat/mergeMatterDocumentIdsForSend';
import { useLazyGetChatDocumentGetPreviewQuery } from '@/api/chat/chatDocumentGetPreview';
import { isDocumentViewerFileSizeTooLarge } from '@/ui/components/DocumentViewer/constants';
import { isChatSwitch, shouldClearCachedMessages } from '../utils/isChatSwitch';
export interface UseChatOptions {
  /** Passed to every `sendMessage` call, e.g. persistent context such as selected case file ids. */
  defaultSendOptions?: SendMessageOptions;

  /** Called once a persisted chat id exists (e.g. after a deferred send), for pages that navigate then. */
  onChatEnsured?: (chatId: string) => void;

  /**
   * Maps the matter/case document IDs behind the selected rows to display metadata for the user bubble.
   */
  resolveMatterQueryDocuments?: (documentIds: string[]) => MessageAttachedDocument[];

  /** Don't fall back to the store's last chat id when there's no `chatId` in the route (e.g. Aerial). */
  disableStoreChatId?: boolean;

  /**
   * Keeps an unsent prompt and restores it when the user returns. Separates new-chat prompts from
   * different places — see `getUnsentPromptKey`. Omit to always start empty.
   */
  unsentPromptScope?: string;

  /** Where to send the user when the chat can't be opened. Defaults to the new-chat page. */
  chatUnavailablePath?: string;

  /** Where a chat this hook creates opens. Defaults to `/chat/:chatId`. */
  chatPath?: (chatId: string) => string;
}

export type UseChatReturn = ReturnType<typeof useChat>;

export type HandleSendMessageMeta = {
  suggestionArrow?: SuggestionArrowKind;
  messageType?: ChatSendMessageType;
};

export const useChat = (options?: UseChatOptions) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { createNewChat, isLoading: isCreatingChat } = useCreateNewChat();
  const { chatId: paramChatId } = useParams();
  const storeChatId = useAppSelector(selectChatId);
  const chatId = paramChatId ?? (options?.disableStoreChatId ? undefined : storeChatId);
  const chatUnavailablePath = options?.chatUnavailablePath ?? `/${AppRoutes.Chat}`;
  const { chatQuery, setChatQuery, switchUnsentPrompt, discardUnsentPrompt } = useUnsentPrompt({
    chatId,
    scope: options?.unsentPromptScope,
  });
  /** Ref copy of `chatQuery`, so the handlers don't change identity on every keystroke. */
  const chatQueryRef = useRef(chatQuery);
  chatQueryRef.current = chatQuery;
  // Chat id we already showed the "no access" / "not found" toast for.
  const deniedChatIdRef = useRef<string | null>(null);
  const { messages, sendMessage, isLoading, setMessages, cancelStreaming, chatCitations, canEditChat } =
    useChatMessages({
      chatId: chatId as string,
      setChatQuery,
    });

  const cachedImprovedPrompts = useAppSelector((state) => selectImprovedPromptsForChat(state, chatId || draftChat));
  const chatDocumentsDownload = useAppSelector(selectChatDocumentsDownload);
  const unviewableDocumentIds = useAppSelector(selectUnviewableDocumentIds);
  const [getChatDocumentDownload, { isFetching: isChatDocumentDownloading }] = useLazyGetChatDocumentGetPreviewQuery();

  const { currentData: chatDocumentsData, error: chatDocumentsError } = useGetChatDocumentsQuery(
    { chatId },
    { skip: !chatId, refetchOnMountOrArgChange: true },
  );

  useEffect(() => {
    if (!isRtkFetchErrorWithStatus(chatDocumentsError)) return;

    const status = (chatDocumentsError as { status: number }).status;

    if (![400, 404, 422, 403].includes(status)) return;

    // This effect can run more than once for the same denied chat; show the toast and redirect only once.
    if (!chatId || deniedChatIdRef.current === chatId) return;
    deniedChatIdRef.current = chatId;

    globalToastService.show({
      closable: true,
      severity: 'error',
      detail:
        status === 403
          ? 'You have not been granted access to this chat'
          : 'This chat does not exist or has been deleted',
      life: 6000,
    });

    // Also drop the persisted chat id — otherwise the redirect below still falls back to
    // this same inaccessible chat via `storeChatId` and the query fails again. The URL still
    // names the chat until the redirect lands, so stop the chat page copying it back meanwhile.
    dispatch(setIgnoreSharedLinkChatIdSync(true));
    dispatch(clearChat());
    navigate(chatUnavailablePath);
  }, [chatDocumentsError, chatId, chatUnavailablePath, dispatch, navigate]);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const resetStickToBottomRef = useRef<() => void>(() => {});
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [fileErrors, setFileErrors] = useState<Record<string, string>>({});
  const [originalPromptBeforeImprove, setOriginalPromptBeforeImprove] = useState<string | null>(null);
  const [improvedPrompt, setImprovedPrompt] = useState<string | null>(null);
  const [hasUneditedImprovedPrompt, setHasUneditedImprovedPrompt] = useState(false);
  const [lastImprovedPromptPair, setLastImprovedPromptPair] = useState<Partial<ImprovedPromptEntry> | null>(null);
  const autoSendDeferredRef = useRef(false);
  const clarifierCancelledRef = useRef(false);

  const [clarifiersMutation, { isLoading: isFetchingClarifiers }] = useImprovePromptClarifiersMutation();
  const [clarifierQuestions, setClarifierQuestions] = useState<ClarifierQuestion[] | null>(null);
  const [pendingMatterDocumentIds, setPendingMatterDocumentIds] = useState<string[]>([]);

  /** The chat's matter, from the loaded chat (set on the case page and for whole-case chats). */
  const contextMatterId = useMemo(() => {
    const ctx = (options?.defaultSendOptions?.context ?? {}) as Record<string, unknown>;
    return typeof ctx.matterId === 'string' ? ctx.matterId : undefined;
  }, [options?.defaultSendOptions?.context]);

  const { onFilesStartUpload, deleteChatDocument, chatDocuments } = useChatFilesUpload(chatId || null, contextMatterId);

  const requestPersistedChatUrlSync = useCallback(
    (id: string | undefined) => {
      if (!id || paramChatId) return;
      dispatch(setPendingUrlSyncChatId({ chatId: id }));
    },
    [dispatch, paramChatId],
  );

  const sessionDocuments = useAppSelector(selectSessionChatDocuments);
  const [deferredSend, setDeferredSend] = useState<{
    chatId: string;
    prompt: string;
    selectedFileIds?: string[];
    selectedHistoryRowIds?: string[];
    messageType?: ChatSendMessageType;
  } | null>(null);

  const pendingSuggestionPickRef = useRef<{ source: SuggestionArrowKind; prompt: string } | null>(null);
  const pendingToolkitPromptPickRef = useRef<PendingToolkitPromptPick | null>(null);

  const lastImprovedPrompt = cachedImprovedPrompts.length
    ? [...cachedImprovedPrompts].sort(sortByUpdatedAtDesc)[0]
    : undefined;
  const pendingMessage = useAppSelector((state) => selectPendingMessageForChat(state, chatId || draftChat));
  const deferredSendFromStore = useAppSelector((state) => selectDeferredSendForChat(state, chatId || draftChat));
  const deferredSendErrorFromStore = useAppSelector((state) =>
    selectDeferredSendErrorForChat(state, chatId || draftChat),
  );

  // Before the chat exists, the draft key stands in for abort controllers and the improved-prompt cache.
  const abortChatKey = chatId || draftChat;

  useEffect(() => {
    pendingSuggestionPickRef.current = null;
    pendingToolkitPromptPickRef.current = null;
  }, [chatId]);

  const hasPendingSynthetic = useMemo(() => {
    if (!pendingMessage || !pendingMessage.userPrompt) return false;
    const hasUserPromptAlready = messages.some(
      (msg) => msg.role === MessageRole.User && msg.status !== 'FAILED' && msg.content === pendingMessage.userPrompt,
    );
    return !hasUserPromptAlready;
  }, [messages, pendingMessage]);

  const isWaitingForDocumentProcessing =
    Boolean(deferredSend || deferredSendFromStore) &&
    hasPendingSynthetic &&
    !isLoading &&
    pendingMessage?.isStreaming === false;

  useRestoreDeferredSendAfterNavigation({
    chatId,
    deferredSend,
    deferredSendFromStore,
    pendingMessage,
    setDeferredSend,
  });

  useRestoreSessionDocumentsForDeferredSend({
    deferredSendFromStore,
    pendingMessage,
    sessionDocuments,
    chatDocuments,
    isWaitingForDocumentProcessing,
  });

  useRestorePromptAfterDeferredSendError({
    chatId,
    draftChatKey: draftChat,
    deferredSendError: deferredSendErrorFromStore,
    dispatch,
    setChatQuery,
  });

  const sessionDocumentsForPrompt = useMemo(
    () => sessionDocuments.filter((d) => d.isPromptAttachment !== false),
    [sessionDocuments],
  );

  const isAnyDocumentUploading = useMemo(
    () => sessionDocumentsForPrompt.some((d) => d.status === FileStatus.UPLOADING),
    [sessionDocumentsForPrompt],
  );

  const isAnyDocumentProcessing = useMemo(
    () => sessionDocumentsForPrompt.some((d) => isProcessingStatus(d.status)),
    [sessionDocumentsForPrompt],
  );

  const hasAnyDocumentFailed = useMemo(
    () => sessionDocumentsForPrompt.some((d) => ERROR_STATUS_VALUES.includes(d.status as FileStatus)),
    [sessionDocumentsForPrompt],
  );

  const { anyFailed: hasAnyPolledFailure } = useChatFileProcessingPolling(sessionDocumentsForPrompt);

  const combinePromptAndMatterAttachments = useCallback(
    (
      promptDocs: Array<
        Pick<MessageAttachedDocument, 'documentId' | 'documentName'> & Partial<MessageAttachedDocument>
      >,
      matterDocumentIds: string[] | undefined,
    ) => {
      const matterIds = matterDocumentIds?.length ? matterDocumentIds : [];
      if (matterIds.length === 0) {
        return promptDocs as MessageAttachedDocument[];
      }
      const fromResolver = options?.resolveMatterQueryDocuments?.(matterIds);
      const byMatterId = new Map((fromResolver ?? []).map((d) => [d.documentId, d]));
      const matterDocs: MessageAttachedDocument[] = matterIds.map((id) => {
        const resolved = byMatterId.get(id);
        return resolved ?? { documentId: id, documentName: 'File' };
      });
      const promptIdSet = new Set(promptDocs.map((d) => d.documentId));
      return [
        ...(promptDocs as MessageAttachedDocument[]),
        ...matterDocs.filter((d) => !promptIdSet.has(d.documentId)),
      ];
    },
    [options],
  );

  const pendingMatterDocuments = useMemo(
    () => combinePromptAndMatterAttachments([], pendingMatterDocumentIds),
    [combinePromptAndMatterAttachments, pendingMatterDocumentIds],
  );

  const applyPendingMatterDocumentIds = useCallback((documentIds: string[]) => {
    const trimmed = normalizePendingMatterDocumentIds(documentIds);
    if (trimmed.length === 0) return;
    setPendingMatterDocumentIds((prev) => [...new Set([...prev, ...trimmed])]);
  }, []);

  const removePendingMatterDocument = useCallback((documentId: string) => {
    setPendingMatterDocumentIds((prev) => prev.filter((id) => id !== documentId));
  }, []);

  const restoreLatestImprovedPromptFromCache = useCallback(() => {
    if (!lastImprovedPrompt) return;

    setOriginalPromptBeforeImprove(lastImprovedPrompt.original);
    setImprovedPrompt(lastImprovedPrompt.improved);
    setChatQuery(lastImprovedPrompt.improved);
    setHasUneditedImprovedPrompt(true);
    setLastImprovedPromptPair({
      original: lastImprovedPrompt.original,
      improved: lastImprovedPrompt.improved,
      isFinished: lastImprovedPrompt.isFinished,
    });
  }, [lastImprovedPrompt, setChatQuery]);

  useEffect(restoreLatestImprovedPromptFromCache, [restoreLatestImprovedPromptFromCache]);

  const [improvePromptMutation, { isLoading: isImprovingPrompt }] = useImprovePromptMutation();
  const previousChatIdRef = useRef<string | undefined>(undefined);
  const hasTrackedChatIdRef = useRef(false);

  // Mirrors `abortChatKey` so async handlers can tell whether the user moved to
  // another chat while their request was in flight.
  const abortChatKeyRef = useRef(abortChatKey);
  useEffect(() => {
    abortChatKeyRef.current = abortChatKey;
  }, [abortChatKey]);

  // Wrapped so the chat API object keeps its identity across renders — see `chatApi` below.
  const handleInputChange = useEventCallback((event: ChangeEvent<HTMLTextAreaElement>) => {
    const value = event.target.value;
    setChatQuery(value);

    if (improvedPrompt && value !== improvedPrompt) {
      // An edited improved prompt is no longer restored from the cache.
      setHasUneditedImprovedPrompt(false);
      if (originalPromptBeforeImprove) {
        dispatch(
          clearImprovedPromptForChat({
            chatId: chatId || draftChat,
            original: originalPromptBeforeImprove,
          }),
        );
      }
    }
  });

  const handleImprovePromptClick = useEventCallback(async () => {
    const currentQuery = chatQueryRef.current;
    if (!currentQuery.trim() || isImprovingPrompt || isFetchingClarifiers) {
      return;
    }

    const normalizedPrompt = normalizeMarkdownBullets(currentQuery);

    setHasUneditedImprovedPrompt(false);
    setOriginalPromptBeforeImprove(normalizedPrompt);

    if (
      lastImprovedPromptPair &&
      lastImprovedPromptPair.original === normalizedPrompt &&
      lastImprovedPromptPair.isFinished
    ) {
      const cachedImprovedPrompt = lastImprovedPromptPair.improved;
      setImprovedPrompt(cachedImprovedPrompt ?? null);
      setChatQuery(cachedImprovedPrompt ?? '');
      setHasUneditedImprovedPrompt(true);
      return;
    }

    // Fetch clarifier questions first
    clarifierCancelledRef.current = false;
    const requestChatKey = abortChatKey;
    try {
      const clarifierPromise = clarifiersMutation({
        originalPrompt: normalizedPrompt,
        chatId: chatId || null,
      });

      // Store in abort store so Stop button can abort the in-flight request
      setImprovePromptAbortController(requestChatKey, clarifierPromise);

      const { questions } = await clarifierPromise.unwrap();

      // If user pressed Stop while we were waiting, don't proceed
      if (clarifierCancelledRef.current) return;

      clearImprovePromptAbortController(requestChatKey);

      // The user opened another chat while we were waiting: the questions belong
      // to the prompt they left behind, so they must not pop up here.
      if (abortChatKeyRef.current !== requestChatKey) return;

      if (questions && questions.length > 0) {
        setClarifierQuestions(questions);
        return;
      }

      // No questions returned - proceed directly to improve
      await runImprovePrompt(normalizedPrompt);
    } catch (error) {
      if (clarifierCancelledRef.current) return;
      if (!(error instanceof DOMException && error.name === 'AbortError')) {
        console.error('Failed to fetch clarifiers:', error);
      }
      // Fallback: run improve prompt without clarifiers
      await runImprovePrompt(normalizedPrompt);
    }
  });

  const runImprovePrompt = useCallback(
    async (normalizedPrompt: string, answeredQuestions?: ImprovePromptQuestionAnswer[]) => {
      const requestChatKey = abortChatKey;
      const promise = improvePromptMutation({
        prompt: normalizedPrompt,
        chatId: chatId || null,
        questions: answeredQuestions,
      });

      dispatch(
        setImprovedPromptForChat({
          chatId: requestChatKey,
          original: normalizedPrompt,
          improved: normalizedPrompt,
          isImproving: true,
          isFinished: false,
        }),
      );

      setImprovePromptAbortController(requestChatKey, promise);

      try {
        const { improvedPrompt: nextImprovedPrompt } = await promise.unwrap();
        // Keep the cache keyed to the chat the prompt was improved for, so it is
        // restored if the user comes back to it.
        dispatch(
          setImprovedPromptForChat({
            chatId: requestChatKey,
            original: normalizedPrompt,
            improved: nextImprovedPrompt,
            isImproving: false,
            isFinished: true,
          }),
        );

        // The user opened another chat while the request was in flight — never
        // push the previous chat's prompt into the current composer.
        if (abortChatKeyRef.current !== requestChatKey) return;

        setImprovedPrompt(nextImprovedPrompt);
        setLastImprovedPromptPair({ original: normalizedPrompt, improved: nextImprovedPrompt, isFinished: true });
        setChatQuery(nextImprovedPrompt);
        setHasUneditedImprovedPrompt(true);
      } catch (error) {
        if (!(error instanceof DOMException && error.name === 'AbortError')) {
          console.error('Failed to improve prompt:', error);
        }
      } finally {
        clearImprovePromptAbortController(requestChatKey);
      }
    },
    [abortChatKey, chatId, dispatch, improvePromptMutation, setChatQuery],
  );

  const handleClarifierAnswersComplete = useCallback(
    async (selectedAnswers: SelectedAnswer[]) => {
      const questionsSnapshot = clarifierQuestions;
      setClarifierQuestions(null);

      const normalizedPrompt = originalPromptBeforeImprove || normalizeMarkdownBullets(chatQueryRef.current);

      if (selectedAnswers.length === 0) {
        // User skipped all questions — proceed without answers
        await runImprovePrompt(normalizedPrompt);
        return;
      }

      // Build structured question/answer pairs (skipped questions are omitted)
      const answeredQuestions: ImprovePromptQuestionAnswer[] = selectedAnswers.map((sa) => ({
        question: questionsSnapshot?.[sa.questionIndex]?.question ?? '',
        answer: sa.answer,
      }));

      await runImprovePrompt(normalizedPrompt, answeredQuestions);
    },
    [clarifierQuestions, originalPromptBeforeImprove, runImprovePrompt],
  );

  const handleRevertPromptClick = useCallback(() => {
    if (originalPromptBeforeImprove == null) {
      return;
    }

    setChatQuery(originalPromptBeforeImprove);
    setImprovedPrompt(null);
    setHasUneditedImprovedPrompt(false);
    dispatch(
      clearImprovedPromptForChat({
        chatId: chatId || draftChat,
        original: originalPromptBeforeImprove,
      }),
    );
  }, [chatId, dispatch, originalPromptBeforeImprove, setChatQuery]);

  const isImprovingPromptFromStore = lastImprovedPrompt?.isImproving === true;

  const isRevertVisible =
    improvedPrompt &&
    hasUneditedImprovedPrompt &&
    chatQuery === improvedPrompt &&
    lastImprovedPrompt?.original !== lastImprovedPrompt?.improved;

  const handleStopImprovingPrompt = useCallback(() => {
    // Mark clarifier as cancelled so the in-flight async handler won't proceed
    clarifierCancelledRef.current = true;

    // Abort any in-flight improve prompt or clarifier request (even across navigation)
    const controller = getImprovePromptAbortController(abortChatKey);
    if (controller) {
      controller.abort();
      clearImprovePromptAbortController(abortChatKey);
    }

    // Revert back to the original prompt immediately
    if (originalPromptBeforeImprove != null) {
      setChatQuery(originalPromptBeforeImprove);

      dispatch(
        clearImprovedPromptForChat({
          chatId: abortChatKey,
          original: originalPromptBeforeImprove,
        }),
      );
    }

    setImprovedPrompt(null);
    setHasUneditedImprovedPrompt(false);
    setClarifierQuestions(null);
  }, [abortChatKey, dispatch, originalPromptBeforeImprove, setChatQuery]);

  const handleSendMessage = useCallback(
    async (
      selectedFileIds?: string[],
      promptOverride?: string,
      forceChatId?: string,
      sendMeta?: HandleSendMessageMeta,
      selectedHistoryRowIds?: string[],
    ) => {
      let rawPrompt = (promptOverride ?? chatQueryRef.current).trim();
      const ctx = (options?.defaultSendOptions?.context ?? {}) as Record<string, unknown>;
      const promptFromContext = typeof ctx.prompt === 'string' ? ctx.prompt : undefined;
      if (promptFromContext) {
        rawPrompt = promptFromContext;
      }

      if (!rawPrompt) return;

      const pickMessageType = (raw: string) =>
        resolveChatSendMessageType({
          precomputed: sendMeta?.messageType,
          normalizedPrompt: normalizeMarkdownBullets(raw),
          normalize: normalizeMarkdownBullets,
          suggestionArrow: sendMeta?.suggestionArrow,
          improvedPrompt,
          hasUneditedImprovedPrompt,
          messages,
          pendingSuggestion: pendingSuggestionPickRef.current,
          pendingToolkitPrompt: pendingToolkitPromptPickRef.current,
        });

      let currentChatId = forceChatId ?? chatId;
      const matterIdsForSend = mergeMatterDocumentIdsForSend(selectedFileIds, pendingMatterDocumentIds);
      const historyRowIdsForSend = selectedHistoryRowIds ?? [];

      resetStickToBottomRef.current();

      const hasErrors = sessionDocuments.some((doc) => ERROR_STATUS_VALUES.includes(doc.status as FileStatus));

      if (hasErrors) {
        return;
      }

      // Like the disabled send button: the backend rejects a prompt whose file is still uploading.
      if (isAnyDocumentUploading) return;

      // Before the first await: the chat page opens on the same draft key and would restore it.
      if (originalPromptBeforeImprove) {
        dispatch(clearImprovedPromptForChat({ chatId: abortChatKey, original: originalPromptBeforeImprove }));
      }

      // New-chat flow: if we don't have a chat yet and documents are still processing,
      // defer *creating the chat* until processing completes (no navigation).
      if (!currentChatId && isAnyDocumentProcessing) {
        // Repeated sends while processing overwrite the deferred draft.
        const outgoingMessageType = pickMessageType(rawPrompt);
        pendingSuggestionPickRef.current = null;
        pendingToolkitPromptPickRef.current = null;
        setDeferredSend({
          chatId: '',
          prompt: rawPrompt,
          selectedFileIds: matterIdsForSend,
          selectedHistoryRowIds: historyRowIdsForSend,
          messageType: outgoingMessageType,
        });
        setChatQuery('');

        const sessionPending: MessageAttachedDocument[] = sessionDocumentsForPrompt;
        const pendingAttachments = combinePromptAndMatterAttachments(sessionPending, matterIdsForSend);

        // Persist draft pending/deferred state so it can continue off-screen.
        dispatch(
          setPendingMessageForChat({
            chatId: draftChat,
            userPrompt: rawPrompt,
            isStreaming: false,
            attachedDocuments: pendingAttachments,
          }),
        );

        const matterIdFromContext = typeof ctx.matterId === 'string' ? ctx.matterId : undefined;

        dispatch(
          setDeferredSendForChat({
            chatId: draftChat,
            prompt: rawPrompt,
            documentIds: sessionDocumentsForPrompt.map((d) => d.documentId),
            createChat: true,
            caseId: matterIdFromContext,
            selectedFileIds: matterIdsForSend,
            selectedHistoryRowIds: historyRowIdsForSend,
            existingDocumentIds: sessionDocumentsForPrompt.map((d) => d.documentId),
            messageAttachmentDocuments: pendingAttachments,
            messageType: outgoingMessageType,
          }),
        );
        return;
      }

      if (!currentChatId && !isCreatingChat) {
        const ctx = (options?.defaultSendOptions?.context ?? {}) as Record<string, unknown>;
        const matterIdFromContext = typeof ctx.matterId === 'string' ? ctx.matterId : undefined;

        // If we are in a matter context, ensure the chat is created with `matter_id`
        // so the backend allows `newlySelectedHistoryRowIds` on the subsequent send.
        currentChatId =
          (await createNewChat({
            ...(matterIdFromContext ? { caseId: matterIdFromContext } : {}),
            chatPath: options?.chatPath,
          })) ?? currentChatId;
      }

      // Existing-chat flow: if files are still processing, show an optimistic UI and defer the real send
      // until processing completes.
      if (isAnyDocumentProcessing) {
        if (!currentChatId) return;
        requestPersistedChatUrlSync(currentChatId);
        // Repeated sends while processing overwrite the deferred send.
        const outgoingMessageType = pickMessageType(rawPrompt);
        pendingSuggestionPickRef.current = null;
        pendingToolkitPromptPickRef.current = null;
        const sessionPending: MessageAttachedDocument[] = sessionDocumentsForPrompt;
        const pendingAttachments = combinePromptAndMatterAttachments(sessionPending, matterIdsForSend);

        dispatch(
          setPendingMessageForChat({
            chatId: currentChatId,
            userPrompt: rawPrompt,
            isStreaming: false,
            attachedDocuments: pendingAttachments,
          }),
        );

        // Persist the deferred send in Redux so it can continue off-screen.
        dispatch(
          setDeferredSendForChat({
            chatId: currentChatId,
            prompt: rawPrompt,
            documentIds: sessionDocumentsForPrompt.map((d) => d.documentId),
            selectedFileIds: matterIdsForSend,
            selectedHistoryRowIds: historyRowIdsForSend,
            existingDocumentIds: Array.from(
              new Set([...chatDocuments.map((e) => e.documentId), ...sessionDocuments.map((e) => e.documentId)]),
            ),
            messageAttachmentDocuments: pendingAttachments,
            messageType: outgoingMessageType,
          }),
        );

        setDeferredSend({
          chatId: currentChatId,
          prompt: rawPrompt,
          selectedFileIds: matterIdsForSend,
          selectedHistoryRowIds: historyRowIdsForSend,
          messageType: outgoingMessageType,
        });
        setChatQuery('');
        return currentChatId;
      }

      const promptAttachmentDocs = sessionDocuments.filter((doc) => doc.isPromptAttachment !== false);

      const allFiles = new Set([
        ...chatDocuments.map((e) => e.documentId),
        ...sessionDocuments.map((e) => e.documentId),
      ]);

      const messageAttachmentDocuments = combinePromptAndMatterAttachments(promptAttachmentDocs, matterIdsForSend);

      const baseOptions: SendMessageOptions = {
        existingDocumentIds: [...allFiles],
        newlySelectedHistoryRowIds: historyRowIdsForSend,
        messageAttachmentDocuments,
      };

      const outgoingMessageType = pickMessageType(rawPrompt);
      pendingSuggestionPickRef.current = null;
      pendingToolkitPromptPickRef.current = null;

      const mergedOptions: SendMessageOptions = {
        ...options?.defaultSendOptions,
        ...baseOptions,
        context: {
          ...(options?.defaultSendOptions?.context ?? {}),
          ...(baseOptions.context ?? {}),
        },
        messageType: outgoingMessageType,
      };

      const markdownPrompt = normalizeMarkdownBullets(rawPrompt);

      requestPersistedChatUrlSync(currentChatId);
      // Promote before streaming: `streamSendChatMessage` clears `isLoading` while this `await`
      // is unresolved, so leftover session attachments would flash in ChatInput.
      dispatch(promoteSessionDocuments());
      await sendMessage(markdownPrompt, mergedOptions, currentChatId);
      setPendingMatterDocumentIds([]);

      // Clear after send, or a re-render with empty session documents would restore the prompt.
      setDeferredSend(null);
      autoSendDeferredRef.current = false;
      if (currentChatId) {
        dispatch(clearDeferredSendForChat({ chatId: currentChatId }));
      }
      setFileErrors({});

      setImprovedPrompt(null);
      setHasUneditedImprovedPrompt(false);
      return currentChatId;
    },
    [
      chatId,
      abortChatKey,
      sessionDocuments,
      isAnyDocumentUploading,
      isAnyDocumentProcessing,
      sessionDocumentsForPrompt,
      chatDocuments,
      messages,
      createNewChat,
      dispatch,
      isCreatingChat,
      options?.defaultSendOptions,
      options?.chatPath,
      combinePromptAndMatterAttachments,
      originalPromptBeforeImprove,
      pendingMatterDocumentIds,
      requestPersistedChatUrlSync,
      sendMessage,
      improvedPrompt,
      hasUneditedImprovedPrompt,
      setChatQuery,
    ],
  );

  useEffect(() => {
    if (!deferredSend) return;

    // If this deferred send is persisted in Redux, the background worker will handle
    // polling + (optional) chat creation + send. Avoid running the local auto-send logic.
    if (deferredSendFromStore) {
      // Clear the local copy too, or it wakes back up once the worker clears the store entry.
      setDeferredSend(null);
      autoSendDeferredRef.current = false;
      return;
    }

    // If any document fails while we were waiting, restore the prompt and stop.
    if (hasAnyDocumentFailed || hasAnyPolledFailure) {
      if (deferredSend.chatId) {
        dispatch(clearPendingMessageForChat({ chatId: deferredSend.chatId }));
        dispatch(clearDeferredSendForChat({ chatId: deferredSend.chatId }));
      }
      setChatQuery(deferredSend.prompt);
      setDeferredSend(null);
      autoSendDeferredRef.current = false;
      return;
    }

    // If the user removed all prompt-attachment documents while a send is deferred,
    // treat it as a cancellation and restore the prompt.
    if (sessionDocumentsForPrompt.length === 0) {
      // ChatView clears session documents on unmount; for a deferred send that's not a cancel, so keep polling.
      if (deferredSendFromStore) {
        return;
      }
      if (deferredSend.chatId) {
        dispatch(clearPendingMessageForChat({ chatId: deferredSend.chatId }));
        dispatch(clearDeferredSendForChat({ chatId: deferredSend.chatId }));
      }
      setChatQuery(deferredSend.prompt);
      setDeferredSend(null);
      autoSendDeferredRef.current = false;
      return;
    }

    // Wait until all prompt-attachment documents are ready.
    const allReadyForChat =
      sessionDocumentsForPrompt.length > 0 &&
      sessionDocumentsForPrompt.every((d) => d.status === FileStatus.READY_FOR_CHAT);

    if (autoSendDeferredRef.current) return;
    if (isAnyDocumentUploading || isAnyDocumentProcessing) return;
    if (!allReadyForChat) return;

    autoSendDeferredRef.current = true;
    const ensureChatAndSendDeferred = async () => {
      try {
        let ensuredChatId = deferredSend.chatId;
        if (!ensuredChatId) {
          const ctx = (options?.defaultSendOptions?.context ?? {}) as Record<string, unknown>;
          const matterIdFromContext = typeof ctx.matterId === 'string' ? ctx.matterId : undefined;
          ensuredChatId =
            (await createNewChat({
              ...(matterIdFromContext ? { caseId: matterIdFromContext } : {}),
              navigateToChatPage: false,
            })) ?? '';
        }
        if (!ensuredChatId) {
          // Couldn't create a chat; restore the prompt and stop.
          setChatQuery(deferredSend.prompt);
          return;
        }

        if (!deferredSend.chatId) {
          options?.onChatEnsured?.(ensuredChatId);
        }

        await handleSendMessage(
          deferredSend.selectedFileIds,
          deferredSend.prompt,
          ensuredChatId,
          deferredSend.messageType ? { messageType: deferredSend.messageType } : undefined,
          deferredSend.selectedHistoryRowIds,
        );
      } finally {
        setDeferredSend(null);
        autoSendDeferredRef.current = false;
      }
    };

    ensureChatAndSendDeferred();
  }, [
    deferredSend,
    deferredSendFromStore,
    dispatch,
    createNewChat,
    handleSendMessage,
    hasAnyDocumentFailed,
    hasAnyPolledFailure,
    isAnyDocumentProcessing,
    isAnyDocumentUploading,
    options,
    sessionDocumentsForPrompt,
    setChatQuery,
  ]);

  const messagesWithPending = useMemo(() => {
    if (!pendingMessage || !pendingMessage.userPrompt) {
      return messages;
    }

    const hasUserPromptAlready = messages.some(
      (msg) => msg.role === MessageRole.User && msg.status !== 'FAILED' && msg.content === pendingMessage.userPrompt,
    );

    if (hasUserPromptAlready) {
      return messages;
    }

    const pendingUserId = `pending-user-${chatId}`;
    const pendingAssistantId = `pending-assistant-${chatId}`;

    const pendingUserMessage: ChatMessage = {
      id: pendingUserId,
      messageId: pendingUserId,
      role: MessageRole.User,
      content: pendingMessage.userPrompt,
      createdAt: new Date(pendingMessage.createdAt),
      ...(pendingMessage.attachedDocuments?.length ? { attachedDocuments: pendingMessage.attachedDocuments } : {}),
    };

    const pendingAssistantMessage: ChatMessage = {
      id: pendingAssistantId,
      messageId: pendingAssistantId,
      role: MessageRole.Assistant,
      content: '',
      createdAt: new Date(pendingMessage.createdAt),
    };

    return [...messages, pendingUserMessage, pendingAssistantMessage];
  }, [chatId, messages, pendingMessage]);

  const handleStopGeneratingResponse = useCallback(() => {
    const chatKey = chatId || draftChat;
    const isWaitingOnDocuments = isWaitingForDocumentProcessing || Boolean(deferredSend || deferredSendFromStore);

    if (isWaitingOnDocuments) {
      const promptToRestore = deferredSend?.prompt ?? deferredSendFromStore?.prompt ?? pendingMessage?.userPrompt ?? '';
      cancelDeferredChatSend({
        dispatch,
        chatKey,
        prompt: promptToRestore,
        setDeferredSend,
        autoSendDeferredRef,
        setChatQuery,
      });
      return;
    }

    if (isLoading && messages.length >= 2) {
      const previousMessage = messages[messages.length - 2];
      setChatQuery(previousMessage.content);
      setMessages(messages.slice(0, -2));
    }

    cancelStreaming();
  }, [
    cancelStreaming,
    chatId,
    deferredSend,
    deferredSendFromStore,
    dispatch,
    isLoading,
    isWaitingForDocumentProcessing,
    messages,
    pendingMessage?.userPrompt,
    setChatQuery,
    setMessages,
  ]);

  const userPreferences = useAppSelector(selectUserPreferences);
  const enterToSend = userPreferences?.enterToSend ?? false;

  const handleKeyDown = useEventCallback(async (e: KeyboardEvent<HTMLTextAreaElement>) => {
    const isModifiedEnter = isModifiedEnterKey(e);
    const isPlainEnter = isPlainEnterKey(e);

    if (isChatSendShortcut(e, enterToSend)) {
      e.preventDefault();
      await handleSendMessage();
      return;
    }

    const textarea = e.currentTarget;
    const value = textarea.value;
    const selectionStart = textarea.selectionStart ?? 0;
    const selectionEnd = textarea.selectionEnd ?? 0;

    // Only handle rich formatting when there is no selection
    if (selectionStart !== selectionEnd) {
      if (enterToSend && isModifiedEnter) {
        e.preventDefault();
        insertLineBreak(textarea);
      }
      return;
    }

    const applyChange = (nextValue: string, nextCursorPos: number) => {
      // Prevent default browser behavior and keep React state in sync
      e.preventDefault();
      textarea.value = nextValue;
      textarea.selectionStart = textarea.selectionEnd = nextCursorPos;
      setChatQuery(nextValue);
    };

    // Typing "-" and Space at the start of a line makes a "• " bullet.
    if (e.key === ' ' && !e.shiftKey && !e.ctrlKey && !e.metaKey && !e.altKey) {
      const transform = getBulletSpaceTransform(value, selectionStart, selectionEnd);
      if (transform) {
        applyChange(transform.nextValue, transform.nextCursorPos);
        return;
      }
    }

    // Markdown-style bullet list behavior on Enter/Backspace
    const isNewLineShortcut = enterToSend ? isModifiedEnter : isPlainEnter;

    if (isNewLineShortcut) {
      const transform = getBulletEnterTransform(value, selectionStart, selectionEnd);
      if (transform) {
        applyChange(transform.nextValue, transform.nextCursorPos);
        return;
      }
      // No transform: the browser types plain Enter itself, but nothing for Ctrl/Cmd + Enter.
      if (enterToSend) {
        e.preventDefault();
        insertLineBreak(textarea);
      }
      return;
    }

    // Backspace at the start of a bullet removes the bullet and resets alignment
    if (e.key === 'Backspace' && !e.shiftKey && !e.ctrlKey && !e.metaKey && !e.altKey) {
      const transform = getBulletBackspaceTransform(value, selectionStart);
      if (transform) {
        applyChange(transform.nextValue, transform.nextCursorPos);
        return;
      }
    }
  });

  const handleUploadFilesClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useEventCallback((event: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files;
    if (selectedFiles && selectedFiles.length > 0) {
      const filesArray = Array.from(selectedFiles);
      const { valid, invalid } = filterFilesByType(filesArray);

      const errors: Record<string, string> = {};
      invalid.forEach((file) => {
        errors[file.name] = getUnsupportedFormatMessage([file.name]);
      });

      if (invalid.length > 0) {
        globalToastService.show({
          closable: true,
          severity: 'error',
          ...getUnsupportedFormatToast(invalid, valid.length > 0),
          life: 6000,
        });
      }

      // Same name isn't a duplicate (e.g. two `image.png` screenshots), so rename the later file.
      const existingDocumentNames = [...chatDocuments, ...sessionDocuments]
        .filter((doc) => VALID_STATUS_VALUES.includes(doc.status as FileStatus))
        .map((doc) => doc.documentName);
      let uniqueFiles = renameDuplicateFiles(valid, existingDocumentNames);

      // Enforce maximum number of files per query
      const existingValidDocumentsCount = chatDocuments.filter((doc) =>
        VALID_STATUS_VALUES.includes(doc.status as FileStatus),
      ).length;
      const remainingSlots = MAX_FILES_PER_QUERY - existingValidDocumentsCount;

      if (remainingSlots <= 0 && uniqueFiles.length > 0) {
        globalToastService.show({
          closable: true,
          severity: 'error',
          summary: 'Upload failed: Maximum 50 files allowed.',
          life: 6000,
        });
        uniqueFiles = [];
      } else if (remainingSlots > 0 && uniqueFiles.length > remainingSlots) {
        const allowedFiles = uniqueFiles.slice(0, remainingSlots);

        globalToastService.show({
          closable: true,
          severity: 'error',
          summary: 'File limit reached',
          life: 6000,
        });

        uniqueFiles = allowedFiles;
      }

      setFileErrors((prevErrors) => ({ ...prevErrors, ...errors }));

      if (uniqueFiles.length > 0) {
        onFilesStartUpload(uniqueFiles);
      }
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  });

  const handleDeleteFile = useCallback(
    async (documentId: string) => {
      deleteChatDocument(documentId);
      const doc = [...chatDocuments, ...sessionDocuments].find((d) => d.documentId === documentId);
      if (doc) {
        const newErrors = { ...fileErrors };
        delete newErrors[doc.documentName];
        setFileErrors(newErrors);
      }
    },
    [chatDocuments, sessionDocuments, deleteChatDocument, fileErrors],
  );

  // Clear local state when chatId changes (e.g., when creating a new chat)
  useEffect(() => {
    const switchedChat = isChatSwitch({
      previousChatId: previousChatIdRef.current,
      chatId,
      storeChatId,
      hasTrackedChatId: hasTrackedChatIdRef.current,
    });

    if (switchedChat) {
      // Keep the prompt of the chat left and restore the one for the chat now open.
      setChatQuery(switchUnsentPrompt(previousChatIdRef.current));
      setOriginalPromptBeforeImprove(null);
      setImprovedPrompt(null);
      setHasUneditedImprovedPrompt(false);
      setLastImprovedPromptPair(null);
      setClarifierQuestions(null);
      setFileErrors({});
      dispatch(clearDocuments());
      // Unsent documents belong to the previous chat. Switching chats doesn't unmount ChatPage, so clear them here.
      dispatch(clearSessionDocuments());
      // Only the shared draft bucket — a persisted chat's cached messages are its own
      // and hydration has already run by now.
      if (shouldClearCachedMessages(chatId)) {
        setMessages([]);
      }
    }
    hasTrackedChatIdRef.current = true;
    previousChatIdRef.current = chatId;
  }, [chatId, storeChatId, dispatch, setMessages, setChatQuery, switchUnsentPrompt]);

  // Must come after the chat-switch effect above: it clears session documents, and on a return to a
  // chat whose documents are cached a restore that ran first would be wiped straight away.
  useRestoreUnsentChatDocuments({
    chatId,
    documents: chatDocumentsData?.documents,
    messages,
    canEditChat,
  });

  const hasFileErrors = useMemo(
    () => sessionDocumentsForPrompt.some((doc) => ERROR_STATUS_VALUES.includes(doc.status as FileStatus)),
    [sessionDocumentsForPrompt],
  );

  const isDocumentTooLargeForPreview = useCallback(
    (documentId: string) => {
      const document = [...chatDocuments, ...sessionDocuments].find((doc) => doc.documentId === documentId);
      return isDocumentViewerFileSizeTooLarge(document?.sizeMb);
    },
    [chatDocuments, sessionDocuments],
  );

  const resolveKnownDocumentName = useCallback(
    (documentId: string) =>
      [...chatDocuments, ...sessionDocuments].find((doc) => doc.documentId === documentId)?.documentName,
    [chatDocuments, sessionDocuments],
  );

  const isUnviewableDocument = useCallback(
    (documentId: string) =>
      !!unviewableDocumentIds[documentId] ||
      [...chatDocuments, ...sessionDocuments].some(
        (doc) => doc.documentId === documentId && !isDisplayableDocument(doc),
      ),
    [unviewableDocumentIds, chatDocuments, sessionDocuments],
  );

  /** Whether the side panel can show the document's preview. */
  const canPreviewDocument = useCallback(
    async (documentId: string, documentName: string | undefined) => {
      if (isUnviewableDocument(documentId)) return false;
      if (chatDocumentsDownload[documentId] || isDocumentTooLargeForPreview(documentId)) return true;
      const result = await getChatDocumentDownload({
        chatDocumentId: documentId,
        original: isRenderedFromOriginal({ documentName }),
      });
      if (result.isError) {
        if (isRtkFetchErrorWithStatus(result.error)) {
          dispatch(markDocumentUnviewable({ documentId }));
        }
        return false;
      }
      return true;
    },
    [dispatch, getChatDocumentDownload, chatDocumentsDownload, isDocumentTooLargeForPreview, isUnviewableDocument],
  );

  /** Checks the preview can be shown before opening the side panel. Shows a toast and returns false if not. */
  const ensureDocumentPreviewAvailable = useCallback(
    async (documentId: string, documentName?: string) => {
      const name = documentName ?? resolveKnownDocumentName(documentId);
      if (await canPreviewDocument(documentId, name)) return true;
      showDocumentPreviewUnsupportedToast(name);
      return false;
    },
    [canPreviewDocument, resolveKnownDocumentName],
  );

  /**
   * Opens a cited document in the side panel. When the document can't be previewed (e.g. an email body
   * that never converted), shows the details of its history row instead, or a toast if it has no row.
   */
  const handleCitedDocumentClick = useCallback(
    async (settings: ChatDocumentPanelSettings, documentName?: string) => {
      const name = documentName ?? resolveKnownDocumentName(settings.documentId);
      if (await canPreviewDocument(settings.documentId, name)) {
        dispatch(setChatDocumentPanelSettings(settings));
        return;
      }
      const historyRowId = getCitedDocumentHistoryRowId(chatCitations, settings.documentId);
      if (historyRowId && contextMatterId) {
        dispatch(openChatHistoryRowPanel({ historyRowId, matterId: contextMatterId }));
      } else {
        showDocumentPreviewUnsupportedToast(name);
      }
    },
    [dispatch, canPreviewDocument, resolveKnownDocumentName, chatCitations, contextMatterId],
  );

  const handleCitationClick = useCallback(
    async (citation: Citation) => {
      const target = resolveCitationTarget(citation, contextMatterId);
      if (!target) return;

      // A history row has no document, so open its details instead.
      if (target.kind === 'historyRow') {
        dispatch(openChatHistoryRowPanel({ historyRowId: target.historyRowId, matterId: target.matterId }));
        return;
      }

      await handleCitedDocumentClick({
        open: true,
        pageNumber: citation.pageNumber ?? 1,
        documentId: target.documentId,
        highlightText: citation.extract,
        highlightTrigger: Date.now(),
      });
    },
    [dispatch, handleCitedDocumentClick, contextMatterId],
  );

  const handleCitationSeeMoreInViewer = useCallback(
    async (citation: Citation, documentFileName: string) => {
      if (!citation?.documentId) return;

      if (!(await ensureDocumentPreviewAvailable(citation.documentId, documentFileName))) return;

      const safeName = documentFileName.trim() || 'Unknown file';

      dispatch(
        setChatDocumentPanelSettings({
          open: true,
          pageNumber: citation.pageNumber ?? 1,
          documentId: citation.documentId,
          highlightText: citation.extract,
          highlightTrigger: Date.now(),
          citationExtractPreview: true,
          citationPanelTitle: `Citations from file: ${safeName}`,
        }),
      );
    },
    [dispatch, ensureDocumentPreviewAvailable],
  );

  const handleChatDocumentClick = useCallback(
    async (settings: ChatDocumentPanelSettings, documentName?: string) => {
      if (settings.open && !(await ensureDocumentPreviewAvailable(settings.documentId, documentName))) return;
      dispatch(setChatDocumentPanelSettings(settings));
    },
    [dispatch, ensureDocumentPreviewAvailable],
  );

  const handleSuggestionClick = useCallback(
    (prompt: string, source: SuggestionArrowKind = 'follow_up') => {
      if (!prompt) return;
      pendingSuggestionPickRef.current = { prompt, source };
      pendingToolkitPromptPickRef.current = null;
      setChatQuery(prompt);
    },
    [setChatQuery],
  );

  const handleToolkitPromptSelect = useCallback(
    (prompt: string) => {
      if (!prompt) return;
      pendingToolkitPromptPickRef.current = { prompt };
      pendingSuggestionPickRef.current = null;
      setChatQuery(prompt);
    },
    [setChatQuery],
  );

  const isLoadingEffective = isLoading || hasPendingSynthetic;

  const { resetStickToBottom, resumeStickToBottom } = useChatAutoScroll({
    scrollContainerRef,
    messagesEndRef,
    messages: messagesWithPending,
    isLoading: isLoadingEffective,
  });
  resetStickToBottomRef.current = resetStickToBottom;

  /**
   * Prompt state, rebuilt on every keystroke. Kept separate from `chatApi` so typing doesn't
   * re-render every chat consumer.
   */
  const chatQueryValue = useMemo(() => ({ chatQuery, isRevertVisible }), [chatQuery, isRevertVisible]);

  /** Everything except the prompt. Stays stable while typing, since handlers read `chatQueryRef`. */
  const chatApi = useMemo(
    () => ({
      chatId,
      setChatQuery,
      discardUnsentPrompt,
      messages: messagesWithPending,
      chatCitations,
      canEditChat,
      isLoading: isLoadingEffective,
      isWaitingForDocumentProcessing,
      messagesEndRef,
      scrollContainerRef,
      fileInputRef,
      chatDocuments,
      sessionDocuments,
      pendingMatterDocuments,
      pendingMatterDocumentIds,
      applyPendingMatterDocumentIds,
      removePendingMatterDocument,
      fileErrors,
      hasFileErrors,
      isAnyDocumentUploading,
      isImprovingPrompt: isImprovingPromptFromStore,
      isChatDocumentDownloading,
      isFetchingClarifiers,
      clarifierQuestions,
      handleInputChange,
      handleSendMessage,
      handleKeyDown,
      handleImprovePromptClick,
      handleRevertPromptClick,
      handleUploadFilesClick,
      handleFileChange,
      handleDeleteFile,
      handleCitationClick,
      handleCitationSeeMoreInViewer,
      handleChatDocumentClick,
      handleCitedDocumentClick,
      resumeStickToBottom,
      handleSuggestionClick,
      handleToolkitPromptSelect,
      handleStopGeneratingResponse,
      handleStopImprovingPrompt,
      handleClarifierAnswersComplete,
    }),
    [
      chatId,
      setChatQuery,
      discardUnsentPrompt,
      messagesWithPending,
      chatCitations,
      canEditChat,
      isLoadingEffective,
      isWaitingForDocumentProcessing,
      chatDocuments,
      sessionDocuments,
      pendingMatterDocuments,
      pendingMatterDocumentIds,
      applyPendingMatterDocumentIds,
      removePendingMatterDocument,
      fileErrors,
      hasFileErrors,
      isAnyDocumentUploading,
      isImprovingPromptFromStore,
      isChatDocumentDownloading,
      isFetchingClarifiers,
      clarifierQuestions,
      handleInputChange,
      handleSendMessage,
      handleKeyDown,
      handleImprovePromptClick,
      handleRevertPromptClick,
      handleUploadFilesClick,
      handleFileChange,
      handleDeleteFile,
      handleCitationClick,
      handleCitationSeeMoreInViewer,
      handleChatDocumentClick,
      handleCitedDocumentClick,
      resumeStickToBottom,
      handleSuggestionClick,
      handleToolkitPromptSelect,
      handleStopGeneratingResponse,
      handleStopImprovingPrompt,
      handleClarifierAnswersComplete,
    ],
  );

  // Flattened for pages that call `useChat` directly. `chatQueryValue` and `chatApi` are for `ChatProvider`.
  return { ...chatApi, ...chatQueryValue, chatApi, chatQueryValue };
};
